import { useState, useEffect } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Switch } from "@/components/ui/switch";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Textarea } from "@/components/ui/textarea";
import { 
  Plus, 
  Sparkles,
  Crown,
  Settings,
  Link2,
  DollarSign,
  Tag,
  Save,
  Trash2,
  Lock,
  CheckCircle,
  ArrowRight
} from "lucide-react";
import axios from "axios";
import { toast } from "sonner";
import { useAuth, API } from "@/App";

const ServiceAddonsManager = ({ services = [], onUpdate, isPremium = false }) => {
  const { token } = useAuth();
  const [selectedService, setSelectedService] = useState(null);
  const [showConfigModal, setShowConfigModal] = useState(false);
  const [config, setConfig] = useState({
    is_addon: false,
    addon_price: "",
    suggested_with: [],
    upsell_prompt: ""
  });
  const [saving, setSaving] = useState(false);

  const openConfig = (service) => {
    setSelectedService(service);
    setConfig({
      is_addon: service.is_addon || false,
      addon_price: service.addon_price?.toString() || "",
      suggested_with: service.suggested_with || [],
      upsell_prompt: service.upsell_prompt || ""
    });
    setShowConfigModal(true);
  };

  const handleSave = async () => {
    if (!selectedService) return;
    
    setSaving(true);
    try {
      await axios.post(
        `${API}/api/upsell/configure/${selectedService.service_id}`,
        {
          is_addon: config.is_addon,
          addon_price: config.addon_price ? parseFloat(config.addon_price) : null,
          suggested_with: config.suggested_with,
          upsell_prompt: config.upsell_prompt || null
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success("Upsell settings saved!");
      setShowConfigModal(false);
      if (onUpdate) onUpdate();
    } catch (error) {
      if (error.response?.status === 403) {
        toast.error("Smart Upsell is a Premium feature. Upgrade to Pro or Elite.");
      } else {
        toast.error("Failed to save upsell settings");
      }
    } finally {
      setSaving(false);
    }
  };

  const toggleSuggestedService = (serviceId) => {
    setConfig(prev => ({
      ...prev,
      suggested_with: prev.suggested_with.includes(serviceId)
        ? prev.suggested_with.filter(id => id !== serviceId)
        : [...prev.suggested_with, serviceId]
    }));
  };

  // Premium Gate
  if (!isPremium) {
    return (
      <Card className="bg-gradient-to-br from-amber-500/10 to-yellow-500/10 border-amber-500/30">
        <CardContent className="p-8 text-center">
          <div className="w-16 h-16 bg-amber-500/20 rounded-full flex items-center justify-center mx-auto mb-4">
            <Lock className="w-8 h-8 text-amber-400" />
          </div>
          <Badge className="bg-gradient-to-r from-amber-500 to-yellow-600 text-black font-semibold mb-4">
            <Crown className="w-3 h-3 mr-1" />
            Pro/Elite Feature
          </Badge>
          <h3 className="text-xl font-bold text-white mb-2">Smart Upsell & Add-ons</h3>
          <p className="text-gray-400 mb-4">
            Boost revenue by suggesting add-on services during booking. Set up bundle discounts and custom prompts.
          </p>
          <Button className="bg-gradient-to-r from-amber-500 to-yellow-600 hover:from-amber-600 hover:to-yellow-700 text-black">
            Upgrade to Unlock
          </Button>
        </CardContent>
      </Card>
    );
  }

  const addons = services.filter(s => s.is_addon);
  const mainServices = services.filter(s => !s.is_addon);

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 bg-gradient-to-br from-amber-500 to-yellow-600 rounded-xl flex items-center justify-center">
            <Sparkles className="w-5 h-5 text-black" />
          </div>
          <div>
            <h3 className="font-semibold text-white">Smart Upsell Manager</h3>
            <p className="text-sm text-gray-400">Configure add-ons and suggestions</p>
          </div>
        </div>
        <Badge className="bg-amber-500/20 text-amber-400 border-amber-500/30">
          <Crown className="w-3 h-3 mr-1" />
          Premium
        </Badge>
      </div>

      {/* Quick Stats */}
      <div className="grid grid-cols-3 gap-4">
        <Card className="bg-stone-800/50 border-stone-700">
          <CardContent className="p-4 text-center">
            <div className="text-2xl font-bold text-amber-400">{addons.length}</div>
            <div className="text-xs text-gray-400">Add-on Services</div>
          </CardContent>
        </Card>
        <Card className="bg-stone-800/50 border-stone-700">
          <CardContent className="p-4 text-center">
            <div className="text-2xl font-bold text-emerald-400">
              {services.filter(s => s.suggested_with?.length > 0).length}
            </div>
            <div className="text-xs text-gray-400">With Suggestions</div>
          </CardContent>
        </Card>
        <Card className="bg-stone-800/50 border-stone-700">
          <CardContent className="p-4 text-center">
            <div className="text-2xl font-bold text-purple-400">
              {services.filter(s => s.upsell_prompt).length}
            </div>
            <div className="text-xs text-gray-400">Custom Prompts</div>
          </CardContent>
        </Card>
      </div>

      {/* Add-on Services */}
      {addons.length > 0 && (
        <div>
          <h4 className="text-sm font-medium text-gray-400 uppercase tracking-wider mb-3">
            Add-on Services
          </h4>
          <div className="grid gap-3">
            {addons.map(service => (
              <Card 
                key={service.service_id}
                className="bg-amber-500/10 border-amber-500/30 cursor-pointer hover:border-amber-500/50 transition-colors"
                onClick={() => openConfig(service)}
              >
                <CardContent className="p-4">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <Badge className="bg-amber-500/20 text-amber-400">Add-on</Badge>
                      <div>
                        <h5 className="font-medium text-white">{service.name}</h5>
                        <div className="text-sm text-gray-400">
                          {service.duration_minutes} min
                        </div>
                      </div>
                    </div>
                    <div className="text-right">
                      <div className="text-gray-500 line-through text-sm">
                        ${service.base_price?.toFixed(2)}
                      </div>
                      <div className="text-lg font-bold text-emerald-400">
                        ${(service.addon_price || service.base_price)?.toFixed(2)}
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      )}

      {/* Main Services */}
      <div>
        <h4 className="text-sm font-medium text-gray-400 uppercase tracking-wider mb-3">
          All Services - Configure Upsells
        </h4>
        <div className="grid gap-3">
          {services.map(service => (
            <Card 
              key={service.service_id}
              className="bg-stone-800/50 border-stone-700 cursor-pointer hover:border-amber-500/30 transition-colors"
              onClick={() => openConfig(service)}
            >
              <CardContent className="p-4">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 bg-stone-700 rounded-lg flex items-center justify-center">
                      <Tag className="w-5 h-5 text-gray-400" />
                    </div>
                    <div>
                      <div className="flex items-center gap-2">
                        <h5 className="font-medium text-white">{service.name}</h5>
                        {service.is_addon && (
                          <Badge variant="outline" className="text-xs border-amber-500/50 text-amber-400">
                            Add-on
                          </Badge>
                        )}
                      </div>
                      <div className="flex items-center gap-3 text-sm text-gray-400">
                        <span>${service.base_price?.toFixed(2)}</span>
                        <span>•</span>
                        <span>{service.duration_minutes} min</span>
                        {service.suggested_with?.length > 0 && (
                          <>
                            <span>•</span>
                            <span className="text-emerald-400">
                              {service.suggested_with.length} linked
                            </span>
                          </>
                        )}
                      </div>
                    </div>
                  </div>
                  <Button variant="ghost" size="sm" className="text-gray-400 hover:text-white">
                    <Settings className="w-4 h-4" />
                  </Button>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      </div>

      {/* Configuration Modal */}
      <Dialog open={showConfigModal} onOpenChange={setShowConfigModal}>
        <DialogContent className="bg-stone-900 border-stone-700 text-white max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Sparkles className="w-5 h-5 text-amber-400" />
              Configure Upsell: {selectedService?.name}
            </DialogTitle>
          </DialogHeader>
          
          <div className="space-y-6">
            {/* Is Add-on Toggle */}
            <div className="flex items-center justify-between p-4 bg-stone-800/50 rounded-xl">
              <div>
                <Label className="text-white">Mark as Add-on Service</Label>
                <p className="text-xs text-gray-400 mt-1">
                  Add-on services appear as suggestions during booking
                </p>
              </div>
              <Switch
                checked={config.is_addon}
                onCheckedChange={(checked) => setConfig(prev => ({ ...prev, is_addon: checked }))}
              />
            </div>

            {/* Add-on Price */}
            {config.is_addon && (
              <div>
                <Label className="text-gray-300 flex items-center gap-2">
                  <DollarSign className="w-4 h-4 text-emerald-400" />
                  Add-on Price (discounted)
                </Label>
                <Input
                  type="number"
                  step="0.01"
                  value={config.addon_price}
                  onChange={(e) => setConfig(prev => ({ ...prev, addon_price: e.target.value }))}
                  placeholder={`Original: $${selectedService?.base_price?.toFixed(2)}`}
                  className="bg-stone-800 border-stone-600 text-white mt-2"
                />
                <p className="text-xs text-gray-500 mt-1">
                  Leave empty to use regular price. Set lower for bundle discount.
                </p>
              </div>
            )}

            {/* Suggest With */}
            <div>
              <Label className="text-gray-300 flex items-center gap-2 mb-3">
                <Link2 className="w-4 h-4 text-purple-400" />
                Suggest with these services
              </Label>
              <div className="max-h-48 overflow-y-auto space-y-2">
                {services
                  .filter(s => s.service_id !== selectedService?.service_id)
                  .map(service => (
                    <div
                      key={service.service_id}
                      className={`p-3 rounded-lg cursor-pointer transition-colors ${
                        config.suggested_with.includes(service.service_id)
                          ? "bg-purple-500/20 border border-purple-500/50"
                          : "bg-stone-800/50 border border-stone-700 hover:border-purple-500/30"
                      }`}
                      onClick={() => toggleSuggestedService(service.service_id)}
                    >
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-2">
                          {config.suggested_with.includes(service.service_id) && (
                            <CheckCircle className="w-4 h-4 text-purple-400" />
                          )}
                          <span className="text-white">{service.name}</span>
                        </div>
                        <span className="text-gray-400 text-sm">
                          ${service.base_price?.toFixed(2)}
                        </span>
                      </div>
                    </div>
                  ))}
              </div>
            </div>

            {/* Custom Upsell Prompt */}
            <div>
              <Label className="text-gray-300">Custom Upsell Prompt</Label>
              <Textarea
                value={config.upsell_prompt}
                onChange={(e) => setConfig(prev => ({ ...prev, upsell_prompt: e.target.value }))}
                placeholder="e.g., 'Complete your look with a beard trim for just $15!'"
                className="bg-stone-800 border-stone-600 text-white mt-2"
                rows={3}
              />
              <p className="text-xs text-gray-500 mt-1">
                Leave empty for auto-generated prompt
              </p>
            </div>

            {/* Save Button */}
            <div className="flex gap-3">
              <Button
                variant="outline"
                onClick={() => setShowConfigModal(false)}
                className="flex-1 border-stone-600 text-gray-300 hover:bg-stone-800"
              >
                Cancel
              </Button>
              <Button
                onClick={handleSave}
                disabled={saving}
                className="flex-1 bg-gradient-to-r from-amber-500 to-yellow-600 hover:from-amber-600 hover:to-yellow-700 text-black"
              >
                {saving ? (
                  <span className="flex items-center gap-2">
                    <div className="animate-spin w-4 h-4 border-2 border-black border-t-transparent rounded-full"></div>
                    Saving...
                  </span>
                ) : (
                  <span className="flex items-center gap-2">
                    <Save className="w-4 h-4" />
                    Save Settings
                  </span>
                )}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default ServiceAddonsManager;
